/**
 * Monitor store — load() kicks off each resource independently so the
 * page can render partial data as soon as each slice arrives.
 *
 * Uses Redis-backed Celery tasks for data fetching in the backend.
 * On first load, endpoints return empty arrays and queue Celery refresh tasks.
 * On retry (after 2s), endpoints return cached data from Redis.
 */
import { create } from "zustand";
import { get as apiGet, post as apiPost, ApiError } from "@/api/client";
import {
  AlertListSchema,
  MonitorSitesEnvelopeSchema,
  NetworkEventRowListSchema,
  NetworkLicenseRollupListSchema,
  SiteHealthListSchema,
  type Alert,
  type MetricPoint,
  type NetworkEventRow,
  type NetworkLicenseRollup,
  type SiteHealth,
} from "@/api/schemas/monitor";
import { getLogger } from "@/lib/logging";

const logger = getLogger("opus.monitor-store");

type MonitorSectionKey =
  | "alerts"
  | "metrics"
  | "sites"
  | "networkEvents"
  | "licenses";

type MonitorSectionStatus = "idle" | "loading" | "ready" | "error";

type MonitorSectionStatusMap = Record<MonitorSectionKey, MonitorSectionStatus>;

interface MonitorLoadOptions {
  force?: boolean;
  alertsHistoryTimespanSec?: number;
}

const EMPTY_SECTION_STATUS: MonitorSectionStatusMap = {
  alerts: "idle",
  metrics: "idle",
  sites: "idle",
  networkEvents: "idle",
  licenses: "idle",
};

function parseSitesPayload(raw: unknown): {
  sites: SiteHealth[];
  cachedAt: string | null;
  syncStatus?: string;
} {
  if (Array.isArray(raw)) {
    return { sites: SiteHealthListSchema.parse(raw), cachedAt: null };
  }
  const parsed = MonitorSitesEnvelopeSchema.parse(raw);
  return {
    sites: parsed.sites,
    cachedAt: parsed.cachedAt ?? null,
    syncStatus: (parsed as Record<string, unknown>).syncStatus as string | undefined,
  };
}

function toDeviceStatusAlerts(raw: unknown): Alert[] {
  if (!Array.isArray(raw)) return [];

  const statusToSeverity: Record<string, Alert["severity"]> = {
    offline: "critical",
    alerting: "warning",
    dormant: "info",
  };

  const alerts: Alert[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") continue;
    const row = item as Record<string, unknown>;
    const status = String(row.status ?? "").toLowerCase();
    if (!status || status === "online") continue;

    const serial = String(row.serial ?? "");
    const name = String(row.name ?? serial ?? "device");
    const model = String(row.model ?? "?");
    const networkId = String(row.networkId ?? "");
    const networkName = networkId || "(no network)";

    alerts.push({
      id: serial ? `alert-meraki-${serial}` : `alert-meraki-${name}-${networkId}`,
      severity: statusToSeverity[status] ?? "info",
      title: `${name} (${model}) is ${status}`,
      source: `meraki/${String(row._orgName ?? "")}`,
      openedAt: String(row.lastReportedAt ?? ""),
      vendor: "meraki",
      target: networkName,
      detail: {
        serial,
        model,
        publicIp: row.publicIp,
        lanIp: row.lanIp,
        lastSeen: row.lastReportedAt,
        networkId,
      },
    });
  }

  return AlertListSchema.parse(alerts);
}

function toMetricPointsFromAlertsHistory(rows: NetworkEventRow[]): MetricPoint[] {
  return rows.flatMap((row) =>
    row.occurredAt
      ? [{ t: row.occurredAt as string, series: "alerts", value: 1 }]
      : [],
  );
}

function toSitePayloadStatus(payload: {
  sites: SiteHealth[];
  syncStatus?: string;
}): MonitorSectionStatus {
  if (payload.sites.length === 0 && payload.syncStatus === "loading") {
    return "loading";
  }
  return "ready";
}

interface State {
  alerts: Alert[];
  metrics: MetricPoint[];
  alertsHistory: NetworkEventRow[];
  sites: SiteHealth[];
  networkEvents: NetworkEventRow[];
  licenses: NetworkLicenseRollup[];
  /** When each section's data was last cached by the backend (ISO string or null). */
  sitesCachedAt: string | null;
  alertsCachedAt: string | null;
  alertsHistoryCachedAt: string | null;
  networkEventsCachedAt: string | null;
  licensesCachedAt: string | null;
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  sectionStatus: MonitorSectionStatusMap;

  load: (opts?: MonitorLoadOptions) => Promise<void>;
  loadNetworkEvents: (opts?: { force?: boolean; }) => Promise<void>;
  loadLicenses: (opts?: { force?: boolean; }) => Promise<void>;
  /** Acknowledge an alert (optimistic). */
  ack: (id: string) => Promise<void>;
}

export const useMonitor = create<State>((set, get) => {
  const updateOverallStatus = (): void => {
    const { sectionStatus } = get();
    if (
      sectionStatus.alerts === "error" ||
      sectionStatus.metrics === "error" ||
      sectionStatus.sites === "error"
    ) {
      set({ status: "error" });
      return;
    }
    if (
      sectionStatus.alerts === "loading" ||
      sectionStatus.metrics === "loading" ||
      sectionStatus.sites === "loading"
    ) {
      set({ status: "loading" });
      return;
    }
    set({ status: "ready" });
  };

  const refreshDeviceStatuses = async (force = false): Promise<void> => {
    if (!force && get().sectionStatus.alerts === "loading") return;
    set((s) => ({
      error: null,
      sectionStatus: { ...s.sectionStatus, alerts: "loading" },
    }));
    updateOverallStatus();

    const t0 = performance.now();
    try {
      const raw = await apiGet<unknown>("/device/statuses/");
      // Unwrap envelope: {data: [...], cachedAt: str} or legacy bare array
      const data = Array.isArray(raw) ? raw : ((raw as Record<string, unknown>).data ?? []);
      const cachedAt = (!Array.isArray(raw) && raw && typeof raw === "object")
        ? ((raw as Record<string, unknown>).cachedAt as string | null ?? null) : null;

      if (Array.isArray(data) && data.length === 0) {
        logger.info({ retry_in_ms: 2000, force }, "monitor_device_statuses_pending");
        setTimeout(() => void refreshDeviceStatuses(true), 2000);
        return;
      }

      const alerts = toDeviceStatusAlerts(data);
      set((s) => ({
        alerts,
        alertsCachedAt: cachedAt,
        sectionStatus: { ...s.sectionStatus, alerts: "ready" },
      }));
      updateOverallStatus();
      logger.info({ count: alerts.length, cached_at: cachedAt, elapsed_ms: Math.round(performance.now() - t0) }, "monitor_device_statuses_ready");
    } catch (err) {
      const message =
        err instanceof ApiError ? `API error ${err.status}` : err instanceof Error ? err.message : "unknown error";
      logger.warn({ error: message, elapsed_ms: Math.round(performance.now() - t0) }, "monitor_device_statuses_error");
      set((s) => ({
        sectionStatus: { ...s.sectionStatus, alerts: "error" },
        error: `${message} — unable to load device statuses`,
      }));
      updateOverallStatus();
    }
  };

  const refreshAlertsHistory = async (
    force = false,
    timespanSec = 24 * 60 * 60,
  ): Promise<void> => {
    if (!force && get().sectionStatus.metrics === "loading") return;
    set((s) => ({
      error: null,
      sectionStatus: { ...s.sectionStatus, metrics: "loading" },
    }));
    updateOverallStatus();

    const t0 = performance.now();
    try {
      const raw = await apiGet<unknown>("/monitor/alerts/history/", {
        timespan: timespanSec,
      });
      const data = Array.isArray(raw) ? raw : ((raw as Record<string, unknown>).data ?? []);
      const cachedAt = (!Array.isArray(raw) && raw && typeof raw === "object")
        ? ((raw as Record<string, unknown>).cachedAt as string | null ?? null) : null;

      const parsed = NetworkEventRowListSchema.parse(data);
      if (parsed.length === 0) {
        logger.info(
          { retry_in_ms: 2000, force, timespan_sec: timespanSec },
          "monitor_alerts_history_pending",
        );
        setTimeout(() => void refreshAlertsHistory(true, timespanSec), 2000);
        return;
      }

      set((s) => ({
        alertsHistory: parsed,
        alertsHistoryCachedAt: cachedAt,
        metrics: toMetricPointsFromAlertsHistory(parsed),
        sectionStatus: { ...s.sectionStatus, metrics: "ready" },
      }));
      updateOverallStatus();
      logger.info(
        {
          count: parsed.length,
          cached_at: cachedAt,
          timespan_sec: timespanSec,
          elapsed_ms: Math.round(performance.now() - t0),
        },
        "monitor_alerts_history_ready",
      );
    } catch (err) {
      const message =
        err instanceof ApiError ? `API error ${err.status}` : err instanceof Error ? err.message : "unknown error";
      logger.warn({ error: message, elapsed_ms: Math.round(performance.now() - t0) }, "monitor_alerts_history_error");
      set((s) => ({
        sectionStatus: { ...s.sectionStatus, metrics: "error" },
        error: `${message} — unable to load alerts history`,
      }));
      updateOverallStatus();
    }
  };

  const refreshSites = async (force = false): Promise<void> => {
    if (!force && get().sectionStatus.sites === "loading") return;
    set((s) => ({
      error: null,
      sectionStatus: { ...s.sectionStatus, sites: "loading" },
    }));
    updateOverallStatus();

    const t0 = performance.now();
    try {
      const raw = await apiGet<unknown>("/monitor/sites/");
      const payload = parseSitesPayload(raw);
      if (payload.sites.length === 0 && payload.syncStatus === "loading") {
        logger.info({ retry_in_ms: 2000, force }, "monitor_sites_pending");
        setTimeout(() => void refreshSites(true), 2000);
        return;
      }

      set((s) => ({
        sites: payload.sites,
        sitesCachedAt: payload.cachedAt,
        sectionStatus: { ...s.sectionStatus, sites: toSitePayloadStatus(payload) },
      }));
      updateOverallStatus();
      logger.info({ count: payload.sites.length, cached_at: payload.cachedAt, elapsed_ms: Math.round(performance.now() - t0) }, "monitor_sites_ready");
    } catch (err) {
      const message =
        err instanceof ApiError ? `API error ${err.status}` : err instanceof Error ? err.message : "unknown error";
      logger.warn({ error: message, elapsed_ms: Math.round(performance.now() - t0) }, "monitor_sites_error");
      set((s) => ({
        sectionStatus: { ...s.sectionStatus, sites: "error" },
        error: `${message} — unable to load site health`,
      }));
      updateOverallStatus();
    }
  };

  return {
    alerts: [],
    metrics: [],
    alertsHistory: [],
    sites: [],
    networkEvents: [],
    licenses: [],
    sitesCachedAt: null,
    alertsCachedAt: null,
    alertsHistoryCachedAt: null,
    networkEventsCachedAt: null,
    licensesCachedAt: null,
    status: "idle",
    error: null,
    sectionStatus: EMPTY_SECTION_STATUS,

    load: async (opts) => {
      const force = opts?.force ?? false;
      const alertsHistoryTimespanSec =
        opts?.alertsHistoryTimespanSec ?? 24 * 60 * 60;
      void refreshDeviceStatuses(force);
      void refreshAlertsHistory(force, alertsHistoryTimespanSec);
      void refreshSites(force);
    },

    loadNetworkEvents: async (opts) => {
      if (!opts?.force && get().sectionStatus.networkEvents === "loading") return;
      set((s) => ({
        error: null,
        sectionStatus: {
          ...s.sectionStatus,
          networkEvents: "loading",
        },
      }));
      try {
        const raw = await apiGet<unknown>("/monitor/network-events/", {
          ...(opts?.force ? { force: true } : {}),
        });
        const evData = Array.isArray(raw) ? raw : ((raw as Record<string, unknown>).data ?? []);
        const evCachedAt = (!Array.isArray(raw) && raw && typeof raw === "object")
          ? ((raw as Record<string, unknown>).cachedAt as string | null ?? null) : null;
        set((s) => ({
          networkEvents: NetworkEventRowListSchema.parse(evData),
          networkEventsCachedAt: evCachedAt,
          sectionStatus: {
            ...s.sectionStatus,
            networkEvents: "ready",
          },
        }));
      } catch (err) {
        const message =
          err instanceof ApiError
            ? `API error ${err.status}`
            : err instanceof Error
              ? err.message
              : "unknown error";
        set((s) => ({
          networkEvents: [],
          sectionStatus: {
            ...s.sectionStatus,
            networkEvents: "error",
          },
          error: `${message} — unable to load network events`,
        }));
      }
    },

    loadLicenses: async (opts) => {
      if (!opts?.force && get().sectionStatus.licenses === "loading") return;
      set((s) => ({
        error: null,
        sectionStatus: {
          ...s.sectionStatus,
          licenses: "loading",
        },
      }));
      try {
        const raw = await apiGet<unknown>("/monitor/licenses/", {
          ...(opts?.force ? { force: true } : {}),
        });
        const licData = Array.isArray(raw) ? raw : ((raw as Record<string, unknown>).data ?? []);
        const licCachedAt = (!Array.isArray(raw) && raw && typeof raw === "object")
          ? ((raw as Record<string, unknown>).cachedAt as string | null ?? null) : null;
        set((s) => ({
          licenses: NetworkLicenseRollupListSchema.parse(licData),
          licensesCachedAt: licCachedAt,
          sectionStatus: {
            ...s.sectionStatus,
            licenses: "ready",
          },
        }));
      } catch (err) {
        const message =
          err instanceof ApiError
            ? `API error ${err.status}`
            : err instanceof Error
              ? err.message
              : "unknown error";
        set((s) => ({
          licenses: [],
          sectionStatus: {
            ...s.sectionStatus,
            licenses: "error",
          },
          error: `${message} — unable to load license rollups`,
        }));
      }
    },

    ack: async (id) => {
      set((s) => ({
        alerts: s.alerts.map((a) => (a.id === id ? { ...a, ackedAt: new Date().toISOString() } : a)),
      }));
      try {
        await apiPost(`/monitor/alerts/${id}/ack/`);
      } catch {
        // Silent — next load() will reconcile.
      }
    },
  };
});
